import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Loader2, CheckCircle2, AlertTriangle, Mic, GitCompare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  useRehearsalByInstance, useCreateOrGetRehearsal, useUploadRehearsalRecording, useRehearsalList, useCreateComparison,
} from "@/lib/rehearsalQueries";
import { usePresentationsList, useStructureVersions, useSections } from "@/lib/presentationQueries";
import { usePresenceContexts } from "@/lib/presenceQueries";
import { useInteractionInstance } from "@/lib/practiceQueries";
import WorkDocumentRoomView from "../WorkDocumentRoomView";
import RehearsalPlayback from "./RehearsalPlayback";
import RehearsalMetricsPanel from "./RehearsalMetricsPanel";

/**
 * Master v5 Prompt 24 — the Rehearsal Room renderer. Mounted by RoomShell
 * exactly like every other renderer (same header/autosave/Save&Exit
 * chrome, same generic Submit button — see practice_routes.py's
 * `_finalize_rehearsal_for_instance` hook for what Submit does here).
 *
 * Three phases, driven purely by `rehearsal.status` (never client state):
 *  1. no Rehearsal yet -> pick Presentation Section or Executive Presence
 *     Context, then create it (draft).
 *  2. draft -> upload a recording (Bunny Storage, not Vimeo).
 *  3. recording_uploaded -> waiting for the Room's own Submit button.
 *  4. submitted -> read-only recording + transcript/metrics (polls while
 *     the TranscriptionJob is queued/processing) + the linked reflection/
 *     annotation WorkDocument, editable via the exact same block/lease
 *     system as every other WorkDocument in this app.
 */
export default function RehearsalRecorderRenderer({ workspaceId, instanceId, isViewAsClient }) {
  const { data: instanceEnvelope } = useInteractionInstance(workspaceId, instanceId);
  const { data: detail, isPending } = useRehearsalByInstance(workspaceId, instanceId);
  const rehearsal = detail?.rehearsal;
  const pollJob = rehearsal?.status === "submitted" && detail?.transcription_job && detail.transcription_job.status !== "succeeded" && detail.transcription_job.status !== "failed";

  if (isPending) {
    return <p className="text-ink-soft text-sm" data-testid="rehearsal-loading">Loading…</p>;
  }

  if (!rehearsal) {
    return <RehearsalContextPicker workspaceId={workspaceId} instanceId={instanceId} isViewAsClient={isViewAsClient} />;
  }
  if (rehearsal.status === "draft") {
    return <RehearsalUpload workspaceId={workspaceId} rehearsal={rehearsal} isViewAsClient={isViewAsClient} />;
  }
  return (
    <div className="space-y-5" data-testid="rehearsal-recorder-renderer">
      {detail.context_label && (
        <p className="text-xs text-ink-soft uppercase tracking-wide" data-testid="rehearsal-context-label">{detail.context_label}</p>
      )}
      <RehearsalPlayback workspaceId={workspaceId} fasId={rehearsal.fas_id} />
      {rehearsal.status === "recording_uploaded" && (
        <p className="text-sm text-ink-soft flex items-center gap-1.5" data-testid="rehearsal-awaiting-submit">
          <CheckCircle2 size={14} className="text-terracotta" /> Recording uploaded — use Submit above when you are ready to finalize this attempt.
        </p>
      )}
      {rehearsal.status === "submitted" && (
        <>
          <RehearsalMetricsPanel
            workspaceId={workspaceId} reh_id={rehearsal.reh_id}
            job={detail.transcription_job} transcript={detail.transcript} metrics={detail.metrics}
            poll={pollJob}
          />
          {rehearsal.reflection_document_id && (
            <div>
              <p className="font-serif text-lg text-ink mb-2">Reflection &amp; notes</p>
              <WorkDocumentRoomView workspaceId={workspaceId} docId={rehearsal.reflection_document_id} isViewAsClient={isViewAsClient} />
            </div>
          )}
          <RehearsalComparePicker workspaceId={workspaceId} rehearsal={rehearsal} />
        </>
      )}
      {instanceEnvelope?.instructions && <p className="text-xs text-ink-soft border-t border-ink/10 pt-3">{instanceEnvelope.instructions}</p>}
    </div>
  );
}

function RehearsalContextPicker({ workspaceId, instanceId, isViewAsClient }) {
  const [contextType, setContextType] = useState("presentation");
  const [presId, setPresId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [pscId, setPscId] = useState("");
  const { data: instanceEnvelope } = useInteractionInstance(workspaceId, instanceId);
  const { data: presentations } = usePresentationsList(workspaceId);
  const { data: structureVersions } = useStructureVersions(workspaceId, presId);
  const svId = structureVersions?.items?.[0]?.sv_id;
  const { data: sectionsData } = useSections(workspaceId, presId, svId);
  // useInteractionInstance returns the envelope {instance, assignment_title,
  // instructions, layer_key} — owner_member_id lives on the nested instance.
  const { data: presenceContexts } = usePresenceContexts(workspaceId, instanceEnvelope?.instance?.owner_member_id);
  const create = useCreateOrGetRehearsal(workspaceId);

  const canCreate = contextType === "presentation" ? !!sectionId : !!pscId;

  async function handleCreate() {
    try {
      await create.mutateAsync({
        iinId: instanceId, contextType,
        sectionId: contextType === "presentation" ? sectionId : undefined,
        presenceContextId: contextType === "presence" ? pscId : undefined,
      });
    } catch (err) {
      toast.error(err?.message || "Couldn't start this rehearsal");
    }
  }

  return (
    <div className="space-y-4" data-testid="rehearsal-context-picker">
      <p className="font-serif text-lg text-ink flex items-center gap-2"><Mic size={18} className="text-terracotta" /> What is this rehearsal for?</p>
      <div className="flex gap-2">
        <button
          onClick={() => setContextType("presentation")} disabled={isViewAsClient}
          className={`text-sm px-3 py-1.5 rounded-full border ${contextType === "presentation" ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-ink/15 text-ink-soft"}`}
          data-testid="rehearsal-context-type-presentation"
        >
          A presentation section
        </button>
        <button
          onClick={() => setContextType("presence")} disabled={isViewAsClient}
          className={`text-sm px-3 py-1.5 rounded-full border ${contextType === "presence" ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-ink/15 text-ink-soft"}`}
          data-testid="rehearsal-context-type-presence"
        >
          An executive presence moment
        </button>
      </div>

      {contextType === "presentation" ? (
        <div className="space-y-2">
          <select value={presId} onChange={(e) => { setPresId(e.target.value); setSectionId(""); }} className="w-full border border-ink/15 rounded-md px-2 py-1.5 text-sm bg-white" data-testid="rehearsal-presentation-select">
            <option value="">Choose a presentation…</option>
            {(presentations?.items || []).map((p) => <option key={p.pres_id} value={p.pres_id}>{p.title}</option>)}
          </select>
          {presId && (
            <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} className="w-full border border-ink/15 rounded-md px-2 py-1.5 text-sm bg-white" data-testid="rehearsal-section-select">
              <option value="">Choose a section…</option>
              {(sectionsData?.items || []).map((s) => <option key={s.section_id} value={s.section_id}>{s.title}</option>)}
            </select>
          )}
          {presId && (sectionsData?.items || []).length === 0 && (
            <p className="text-xs text-ink-soft">No sections yet — add one in this presentation&apos;s Structure area first.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <select value={pscId} onChange={(e) => setPscId(e.target.value)} className="w-full border border-ink/15 rounded-md px-2 py-1.5 text-sm bg-white" data-testid="rehearsal-presence-context-select">
            <option value="">Choose a presence context…</option>
            {(presenceContexts?.items || []).map((c) => <option key={c.psc_id} value={c.psc_id}>{c.title}</option>)}
          </select>
          {(presenceContexts?.items || []).length === 0 && (
            <p className="text-xs text-ink-soft">No presence contexts yet — add one in the Presence Lab first.</p>
          )}
        </div>
      )}

      <Button onClick={handleCreate} disabled={!canCreate || create.isPending || isViewAsClient} data-testid="rehearsal-context-confirm-btn">
        {create.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null} Start rehearsal
      </Button>
    </div>
  );
}

function RehearsalUpload({ workspaceId, rehearsal, isViewAsClient }) {
  const upload = useUploadRehearsalRecording(workspaceId, rehearsal.reh_id);
  const [dragOver, setDragOver] = useState(false);

  async function handleFile(file) {
    if (!file) return;
    try {
      await upload.mutateAsync(file);
      toast.success("Recording uploaded");
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    }
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
      className={`border-2 border-dashed rounded-xl p-8 text-center ${dragOver ? "border-terracotta bg-terracotta/5" : "border-ink/20"}`}
      data-testid="rehearsal-upload-dropzone"
    >
      <Upload size={28} className="mx-auto text-ink-soft mb-3" />
      <p className="text-sm text-ink mb-3">Upload a video or audio recording of your rehearsal (mp4, webm, wav, mp3, m4a — up to 25MB).</p>
      <label>
        <input
          type="file" accept=".mp4,.webm,.wav,.mp3,.m4a,audio/*,video/*" className="hidden" disabled={isViewAsClient || upload.isPending}
          onChange={(e) => handleFile(e.target.files?.[0])} data-testid="rehearsal-upload-input"
        />
        <span className="inline-block">
          <Button asChild={false} disabled={isViewAsClient || upload.isPending} data-testid="rehearsal-upload-choose-btn">
            {upload.isPending ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Uploading…</> : "Choose a file"}
          </Button>
        </span>
      </label>
      {upload.isError && (
        <p className="text-xs text-terracotta mt-2 flex items-center justify-center gap-1" data-testid="rehearsal-upload-error">
          <AlertTriangle size={12} /> {upload.error?.message}
        </p>
      )}
    </div>
  );
}


function RehearsalComparePicker({ workspaceId, rehearsal }) {
  const navigate = useNavigate();
  const [otherRehId, setOtherRehId] = useState("");
  const { data: list } = useRehearsalList(workspaceId, { layer_key: rehearsal.layer_key, owner_member_id: rehearsal.owner_member_id });
  const create = useCreateComparison(workspaceId);
  const options = (list?.items || []).filter((r) => r.reh_id !== rehearsal.reh_id && r.status === "submitted");

  if (options.length === 0) return null;

  async function handleCompare() {
    try {
      const result = await create.mutateAsync({ baselineRehId: otherRehId, latestRehId: rehearsal.reh_id });
      navigate(`/rehearsal-room/w/${workspaceId}/rehearsals/compare/${result.comparison.cmp_id}`);
    } catch (err) {
      toast.error(err?.message || "Couldn't compare these attempts");
    }
  }

  return (
    <div className="border-t border-ink/10 pt-4 flex items-center gap-2" data-testid="rehearsal-compare-picker">
      <GitCompare size={15} className="text-ink-soft" />
      <select value={otherRehId} onChange={(e) => setOtherRehId(e.target.value)} className="border border-ink/15 rounded-md px-2 py-1.5 text-sm bg-white" data-testid="rehearsal-compare-select">
        <option value="">Compare with a previous attempt…</option>
        {options.map((r) => (
          <option key={r.reh_id} value={r.reh_id}>
            Attempt from {new Date(r.submitted_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
          </option>
        ))}
      </select>
      <Button size="sm" variant="outline" onClick={handleCompare} disabled={!otherRehId || create.isPending} data-testid="rehearsal-compare-btn">
        {create.isPending ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null} Compare
      </Button>
    </div>
  );
}
